import { useCallback, useMemo } from 'react';

import type { EventHandlers } from '@atlaskit/editor-common/ui';

import { expValEquals } from '@confluence/feature-experiments';
import { isExternalLink } from '@confluence/linking-platform/entry-points/isExternalLink.ts';
import {
	useMatchRoute,
	useRouteActions,
} from '@confluence/route-manager/entry-points/RouteState.ts';

import { createLinkEventHandler } from './createLinkEventHandler.tsx';

type ExternalLinkHandlers = {
	eventHandlers: EventHandlers | undefined;
	onSetLinkTarget: (url: string) => '_blank' | undefined;
};

/**
 * Wires external links inside deprecated macros to open in a new tab, gated by
 * the `confluence_legacy_macro_external_links_new_tab` experiment. Returns the
 * `eventHandlers` and `onSetLinkTarget` props to pass to a nested `ADFRenderer`.
 */
export const useExternalLinkHandlers = (): ExternalLinkHandlers => {
	const { push } = useRouteActions();
	const matchRoute = useMatchRoute();

	const eventHandlers = useMemo(
		() =>
			expValEquals('confluence_legacy_macro_external_links_new_tab', 'isEnabled', true)
				? createLinkEventHandler({ push, matchRoute })
				: undefined,
		[push, matchRoute],
	);

	const onSetLinkTarget = useCallback(
		(url: string) =>
			isExternalLink(url) &&
			expValEquals('confluence_legacy_macro_external_links_new_tab', 'isEnabled', true)
				? '_blank'
				: undefined,
		[],
	);

	return { eventHandlers, onSetLinkTarget };
};
